// © 2025 Camp Promethean, LLC. All Rights Reserved.
//
// useProactiveNudges (T-505, stretch) — the "paperclip notices things."
//
// Opt-in (default OFF), rate-limited, dismissible. Surfaces AT MOST ONE
// conservative, grounded suggestion based on REAL facility aggregates — never a
// fabricated trend. The current grounded check is runoff EC drift: it walks
// rooms → zones → runoff_logs for the active facility (RLS-scoped) and only
// fires when a zone's daily-average EC strictly rises over 3+ recent days by a
// meaningful margin.
//
// Clicking a nudge hands a grounded question to converse; it never writes.

import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useFacility } from "@/contexts/FacilityContext";

const SETTINGS_KEY = "camp_promethean_assistant_nudges";
const STATE_KEY = "camp_promethean_assistant_nudges_state";

// Conservative defaults — this is the feature most likely to annoy.
const MIN_HOURS_BETWEEN_NUDGES = 6;
const MIN_DISTINCT_DAYS = 3;
const MIN_TOTAL_EC_RISE = 0.2;
const LOOKBACK_DAYS = 6;

export interface Nudge {
  /** Stable id so a dismissed nudge stays dismissed for the cooldown window. */
  id: string;
  message: string;
  /** Grounded question handed to converse when the user opens the nudge. */
  question: string;
}

interface NudgeState {
  lastShownAt: number;
  dismissedIds: string[];
}

function loadEnabled(): boolean {
  try {
    return window.localStorage.getItem(SETTINGS_KEY) === "on";
  } catch {
    return false;
  }
}

function loadState(): NudgeState {
  try {
    const raw = window.localStorage.getItem(STATE_KEY);
    if (raw) return JSON.parse(raw) as NudgeState;
  } catch {
    /* ignore */
  }
  return { lastShownAt: 0, dismissedIds: [] };
}

function saveState(state: NudgeState) {
  try {
    window.localStorage.setItem(STATE_KEY, JSON.stringify(state));
  } catch {
    /* ignore */
  }
}

function dayKey(iso: string): string {
  return iso.slice(0, 10);
}

/** True if the series strictly increases by at least MIN_TOTAL_EC_RISE overall. */
function isRisingDrift(values: number[]): boolean {
  if (values.length < MIN_DISTINCT_DAYS) return false;
  for (let i = 1; i < values.length; i++) {
    if (values[i] <= values[i - 1]) return false;
  }
  return values[values.length - 1] - values[0] >= MIN_TOTAL_EC_RISE;
}

async function detectRunoffEcDrift(facilityId: string): Promise<Nudge | null> {
  const { data: rooms } = await supabase
    .from("rooms")
    .select("id, room_name")
    .eq("facility_id", facilityId);
  if (!rooms || rooms.length === 0) return null;

  const roomIds = rooms.map((r) => r.id);
  const { data: zones } = await supabase.from("zones").select("id, room_id").in("room_id", roomIds);
  if (!zones || zones.length === 0) return null;

  const zoneIds = zones.map((z) => z.id);
  const roomNameById = new Map(rooms.map((r) => [r.id, r.room_name as string]));
  const roomIdByZone = new Map(zones.map((z) => [z.id as string, z.room_id as string]));

  const since = new Date(Date.now() - LOOKBACK_DAYS * 86_400_000).toISOString();
  const { data: logs } = await supabase
    .from("runoff_logs")
    .select("zone_id, log_date, ec")
    .in("zone_id", zoneIds)
    .gte("log_date", since)
    .order("log_date", { ascending: true });
  if (!logs || logs.length === 0) return null;

  // Group EC by zone → day, then average per day.
  const byZone = new Map<string, Map<string, { sum: number; n: number }>>();
  for (const row of logs) {
    const ec = row.ec as number | null;
    const zoneId = row.zone_id as string | null;
    const logDate = row.log_date as string | null;
    if (ec == null || !zoneId || !logDate) continue;
    if (!byZone.has(zoneId)) byZone.set(zoneId, new Map());
    const days = byZone.get(zoneId)!;
    const key = dayKey(logDate);
    const cur = days.get(key) ?? { sum: 0, n: 0 };
    days.set(key, { sum: cur.sum + ec, n: cur.n + 1 });
  }

  for (const [zoneId, days] of byZone) {
    const series = [...days.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, v]) => v.sum / v.n);
    if (isRisingDrift(series)) {
      const roomName = roomNameById.get(roomIdByZone.get(zoneId) ?? "") ?? "a room";
      const dayCount = series.length;
      return {
        id: `runoff-ec-drift:${zoneId}:${dayKey(new Date().toISOString())}`,
        message: `Runoff EC in ${roomName} has drifted up ${dayCount} days running.`,
        question: `Runoff EC has been rising in ${roomName} over the last ${dayCount} days. Show me the runoff EC trend by zone for ${roomName} and flag which zones are drifting.`,
      };
    }
  }

  return null;
}

export interface UseProactiveNudgesResult {
  enabled: boolean;
  setEnabled: (on: boolean) => void;
  nudge: Nudge | null;
  dismiss: () => void;
}

export function useProactiveNudges(): UseProactiveNudgesResult {
  const { activeFacility } = useFacility();
  const facilityId = activeFacility?.id ?? null;

  const [enabled, setEnabledState] = useState<boolean>(loadEnabled);
  const [nudge, setNudge] = useState<Nudge | null>(null);
  const stateRef = useRef<NudgeState>(loadState());

  const setEnabled = useCallback((on: boolean) => {
    setEnabledState(on);
    try {
      window.localStorage.setItem(SETTINGS_KEY, on ? "on" : "off");
    } catch {
      /* ignore */
    }
    if (!on) setNudge(null);
  }, []);

  const dismiss = useCallback(() => {
    if (nudge) {
      const s = stateRef.current;
      s.dismissedIds = [...s.dismissedIds.slice(-50), nudge.id];
      s.lastShownAt = Date.now();
      saveState(s);
    }
    setNudge(null);
  }, [nudge]);

  useEffect(() => {
    if (!enabled || !facilityId) {
      setNudge(null);
      return;
    }

    const s = stateRef.current;
    const sinceLast = Date.now() - s.lastShownAt;
    if (sinceLast < MIN_HOURS_BETWEEN_NUDGES * 3_600_000) return;

    let cancelled = false;
    (async () => {
      try {
        const found = await detectRunoffEcDrift(facilityId);
        if (cancelled || !found) return;
        if (s.dismissedIds.includes(found.id)) return;
        s.lastShownAt = Date.now();
        saveState(s);
        setNudge(found);
      } catch {
        // Grounded-only: on any error we simply show nothing.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, facilityId]);

  return { enabled, setEnabled, nudge, dismiss };
}
